import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import CanvasContainer from "../../canvas/CanvasContainer.tsx";
import { GetPublishedSlidesWithContentByTitleIdPublic } from "../../redux/api/universities.api";
import { basicURL } from "../../apiManager/APIConfig";

/** Original maker canvas. Elements keep these coordinates; the stage is scaled as one piece. */
const WEB_STAGE_WIDTH = 1280;
const WEB_STAGE_HEIGHT = 720;
const MAX_USER_ZOOM = 4;

function isEmbeddedViewerTarget(target) {
  return !!(
    target &&
    target.closest &&
    target.closest("iframe, model-viewer, .leaflet-container")
  );
}

// Derive a normalized 3D model URL from a slide (mirrors ShowThreeD logic)
function getModelUrlFromSlide(slide) {
  if (!slide) return null;
  const files = slide?.Files ?? slide?.files ?? [];
  const first = Array.isArray(files) ? files[0] : null;
  let url = first?.url ?? first?.path ?? null;
  if (url != null && typeof url === "string") {
    const trimmed = url.trim();
    if (trimmed) {
      if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
        const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
        url = trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
      } else {
        url = trimmed;
      }
    } else {
      url = null;
    }
  } else {
    url = null;
  }
  return url;
}

function PresentationMobileViewPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const theme = useTheme();
  const allSlides = useSelector((state) => state.uni?.slides || []);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialIndexFromUrl = Number(searchParams.get("slideIndex") || "0");
  const [currentIndex, setCurrentIndex] = useState(
    Number.isNaN(initialIndexFromUrl) || initialIndexFromUrl < 0
      ? 0
      : initialIndexFromUrl
  );

  useEffect(() => {
    if (id && id !== "undefined") {
      GetPublishedSlidesWithContentByTitleIdPublic(id);
    }
  }, [id, dispatch]);

  // Keep in sync when the native app changes ?slideIndex= via WebView reload/nav
  useEffect(() => {
    const next = Number(searchParams.get("slideIndex") || "0");
    if (!Number.isNaN(next) && next >= 0) {
      setCurrentIndex(next);
    }
  }, [location.search]);

  const { currentSlide, canvasData, isThreeDSlide, threeDModelUrl } = useMemo(() => {
    if (!Array.isArray(allSlides) || allSlides.length === 0) {
      return {
        currentSlide: null,
        canvasData: [],
        isThreeDSlide: false,
        threeDModelUrl: null,
      };
    }
    const safeIndex = Math.min(
      Math.max(currentIndex, 0),
      allSlides.length - 1
    );
    const slide = allSlides[safeIndex];
    let data = slide?.content?.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (_) {
        data = [];
      }
    }
    if (data && !Array.isArray(data) && typeof data === "object") {
      if (Array.isArray(data.elements)) data = data.elements;
      else if (Array.isArray(data.data)) data = data.data;
      else data = [];
    }
    const type =
      slide?.parentTitle?.type ||
      slide?.Titles?.type ||
      slide?.title_data?.type ||
      slide?.titles?.type ||
      slide?.type ||
      "";
    const isThreeD = type === "three_d_model";
    const modelUrl = isThreeD ? getModelUrlFromSlide(slide) : null;
    return {
      currentSlide: slide,
      canvasData: Array.isArray(data) ? data : [],
      isThreeDSlide: isThreeD && Boolean(modelUrl),
      threeDModelUrl: modelUrl,
    };
  }, [allSlides, currentIndex]);

  // Force CanvasContainer to remount once slides are loaded so it picks up initialData
  const canvasKey = useMemo(() => {
    if (currentSlide?.id) return currentSlide.id;
    return id || "presentation-mobile-view";
  }, [currentSlide, id]);
  const stageRef = useRef(null);

  // Uniform scale of the whole 1280×720 stage into this WebView's viewport.
  // userZoom starts at 1 (entire slide visible, no pan). Pinch and double-tap
  // raise it. Touches that start on a 3D or histology viewer are left alone.
  useLayoutEffect(() => {
    const stage = stageRef.current || document.getElementById("slide-stage");
    if (!stage) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const meta = document.querySelector('meta[name="viewport"]');
    const prevMeta = meta?.getAttribute("content") || "";
    if (meta) {
      meta.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
      );
    }
    html.style.margin = "0";
    body.style.margin = "0";
    html.style.width = "100%";
    body.style.width = "100%";
    html.style.height = "100%";
    body.style.height = "100%";

    let userZoom = 1;
    let panX = 0;
    let panY = 0;
    const pointers = new Map();
    let mode = null;
    let startDist = 1;
    let startZoom = 1;
    let startPanX = 0;
    let startPanY = 0;
    let startMidX = 0;
    let startMidY = 0;
    let lastTap = 0;

    const layout = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (!vw || !vh) return;
      // Match the website player: scale the 1280×720 stage as one piece.
      // CSS zoom resizes 3D and tile viewers differently from the site.
      userZoom = Math.min(MAX_USER_ZOOM, Math.max(1, userZoom));
      const fit = Math.min(vw / WEB_STAGE_WIDTH, vh / WEB_STAGE_HEIGHT);
      const scale = fit * userZoom;
      const visualW = WEB_STAGE_WIDTH * scale;
      const visualH = WEB_STAGE_HEIGHT * scale;
      if (userZoom <= 1) {
        panX = 0;
        panY = 0;
      }
      let x = (vw - visualW) / 2 + panX;
      let y = (vh - visualH) / 2 + panY;
      if (visualW > vw) x = Math.min(0, Math.max(vw - visualW, x));
      else x = (vw - visualW) / 2;
      if (visualH > vh) y = Math.min(0, Math.max(vh - visualH, y));
      else y = (vh - visualH) / 2;
      const inner = stage.querySelector(".canvas-container");
      if (inner) inner.style.overflow = "hidden";
      stage.style.zoom = "1";
      stage.style.width = `${WEB_STAGE_WIDTH}px`;
      stage.style.height = `${WEB_STAGE_HEIGHT}px`;
      stage.style.position = "fixed";
      stage.style.left = "0px";
      stage.style.top = "0px";
      stage.style.overflow = "hidden";
      stage.style.transformOrigin = "top left";
      stage.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    };

    window.__resetSlideFit = () => {
      userZoom = 1;
      panX = 0;
      panY = 0;
      layout();
    };
    window.__slideFitEngine = true;

    const onStart = (event) => {
      if (isEmbeddedViewerTarget(event.target)) {
        mode = "viewer";
        return;
      }
      Array.from(event.changedTouches || []).forEach((touch) => {
        pointers.set(touch.identifier, { x: touch.clientX, y: touch.clientY });
      });
      if (pointers.size >= 2) {
        const pts = Array.from(pointers.values());
        startDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
        startZoom = userZoom;
        startPanX = panX;
        startPanY = panY;
        startMidX = (pts[0].x + pts[1].x) / 2;
        startMidY = (pts[0].y + pts[1].y) / 2;
        mode = "pinch";
        event.preventDefault();
      }
    };

    const onMove = (event) => {
      if (mode === "viewer" || isEmbeddedViewerTarget(event.target)) return;
      Array.from(event.changedTouches || []).forEach((touch) => {
        if (pointers.has(touch.identifier)) {
          pointers.set(touch.identifier, { x: touch.clientX, y: touch.clientY });
        }
      });
      if (mode === "pinch" && pointers.size >= 2) {
        const pts = Array.from(pointers.values());
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
        userZoom = startZoom * (dist / startDist);
        panX = startPanX + (pts[0].x + pts[1].x) / 2 - startMidX;
        panY = startPanY + (pts[0].y + pts[1].y) / 2 - startMidY;
        event.preventDefault();
        layout();
        return;
      }
      if (userZoom <= 1) {
        event.preventDefault();
        return;
      }
      if (pointers.size === 1) {
        const point = Array.from(pointers.values())[0];
        if (mode !== "pan") {
          mode = "pan";
          startPanX = panX;
          startPanY = panY;
          startMidX = point.x;
          startMidY = point.y;
        }
        panX = startPanX + (point.x - startMidX);
        panY = startPanY + (point.y - startMidY);
        event.preventDefault();
        layout();
      }
    };

    const onEnd = (event) => {
      Array.from(event.changedTouches || []).forEach((touch) => {
        pointers.delete(touch.identifier);
      });
      if (pointers.size > 0) return;
      if (mode !== "viewer" && mode !== "pinch" && mode !== "pan" && !isEmbeddedViewerTarget(event.target)) {
        const now = Date.now();
        if (now - lastTap < 280) {
          if (userZoom > 1.02) {
            userZoom = 1;
            panX = 0;
            panY = 0;
          } else {
            userZoom = 2;
          }
          layout();
          lastTap = 0;
        } else {
          lastTap = now;
        }
      }
      mode = null;
      if (userZoom <= 1.01) {
        userZoom = 1;
        panX = 0;
        panY = 0;
        layout();
      }
    };

    const onDoubleClick = (event) => {
      if (isEmbeddedViewerTarget(event.target)) return;
      if (userZoom > 1.02) {
        userZoom = 1;
        panX = 0;
        panY = 0;
      } else {
        userZoom = 2;
      }
      layout();
    };

    layout();
    window.addEventListener("resize", layout);
    window.addEventListener("orientationchange", layout);
    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        layout();
      });
    };
    const observer = new MutationObserver(schedule);
    observer.observe(stage, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });
    document.addEventListener("touchstart", onStart, { passive: false });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    document.addEventListener("dblclick", onDoubleClick);
    return () => {
      delete window.__resetSlideFit;
      delete window.__slideFitEngine;
      if (meta) meta.setAttribute("content", prevMeta);
      observer.disconnect();
      window.removeEventListener("resize", layout);
      window.removeEventListener("orientationchange", layout);
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
      document.removeEventListener("dblclick", onDoubleClick);
    };
  }, [canvasKey]);

  return (
    <MDBox
      data-slide-fit="scale"
      sx={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        bgcolor: theme.palette.background.default,
        touchAction: "none",
      }}
    >
        <MDBox
          id="slide-stage"
          ref={stageRef}
          sx={{
            width: WEB_STAGE_WIDTH,
            height: WEB_STAGE_HEIGHT,
            position: "absolute",
            left: 0,
            top: 0,
            transformOrigin: "top left",
            bgcolor: theme.palette.background.paper,
          }}
        >
          {isThreeDSlide && threeDModelUrl ? (
            <MDBox
              sx={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
              }}
            >
              <iframe
                key={threeDModelUrl}
                src={
                  typeof window !== "undefined"
                    ? `${window.location.origin}/model-viewer.html?src=${encodeURIComponent(
                        threeDModelUrl
                      )}&theme=${theme.palette.mode === "dark" ? "dark" : "light"}`
                    : ""
                }
                title="3D Model viewer"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  display: "block",
                }}
              />
            </MDBox>
          ) : (
            <CanvasContainer
              key={canvasKey}
              initialData={canvasData}
              isPlayMode
              showAddButtons={false}
              stageWidth={WEB_STAGE_WIDTH}
              stageHeight={WEB_STAGE_HEIGHT}
              viewMode="web"
              clipStage
            />
          )}
        </MDBox>
    </MDBox>
  );
}

export default PresentationMobileViewPage;

